import { Helper } from '@app/helpers/utilities/helper'
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, from, Observable, of, switchMap } from 'rxjs'
import { TokenRepositoryService } from '@services/token-repository.service'
import { environment } from '@environments/environment'
import { AuthService } from '@services/auth.service'

@Injectable()
export class AuthProvidingInterceptor implements HttpInterceptor {
    constructor(
        private tokenRepositoryService: TokenRepositoryService,
        private authService: AuthService
    ) { }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        let headers = request.headers as HttpHeaders
        const isAuthAllowedDomain = Helper.matchString(request.url, environment.authAllowedDomains)
        if (!isAuthAllowedDomain) {
            return next.handle(request)
        }
        if (headers.has('Authorization')) {
            return next.handle(request)
        }
        if (!this.tokenRepositoryService.hasTokens()) {
            return next.handle(request)
        }
        const httpMethodSupportsAuthorisation = request.method !== 'OPTIONS'
        if (!httpMethodSupportsAuthorisation) {
            return next.handle(request)
        }
        if (!this.tokenRepositoryService.accessTokenExpiresSoon()) {
            return next.handle(this.requestWithAuthHeader(request, headers, this.tokenRepositoryService.getAccessToken()))
        }
        return from(this.authService.refreshToken())
            .pipe(
                catchError(() => of(null)),
                switchMap(tokenData => {
                    const token = tokenData?.access_token ?? this.tokenRepositoryService.getAccessToken()
                    return next.handle(this.requestWithAuthHeader(request, headers, token))
                })
            )
    }

    private requestWithAuthHeader(request: HttpRequest<any>, headers: HttpHeaders, token: string | null) {
        if (token === null) {
            return request
        }
        return request.clone({
            headers: headers.append(
                'Authorization',
                `Bearer ${token}`
            ),
        })
    }
}
